import { IconNode } from 'lucide-react'

import { LocationType } from '~/client/graph'

import { DEFAULT_GLYPH_STROKE_WIDTH, iconNodeToPathData } from './iconGeometry'
import {
  getGlyph,
  getPictogramGlyph,
  ICON_DATA,
  isIconArtLoaded,
  LOCATION_ICON_BY_TYPE,
  type LocationIconName,
} from './markerSpec'

import Colors from '~/client/src/shared/theme.module.scss'

const CURSOR_PX = 24
const GRID = 24
const HOTSPOT = CURSOR_PX / 2
// A CSS cursor has no CSS context, so currentColor resolves to nothing — the ink has to be a
// literal. mapBlack is already one; the neutral tokens are get_color() vars.
const INK = Colors.mapBlack
const HALO = Colors.mapWhite
const HALO_EXTRA = 2

export const FALLBACK_CURSOR = 'copy'

// Same paint rule the DOM, the canvas rasterizer and konva follow: a node carrying a fill is
// filled, everything else stroked. Drawn twice so the glyph stays visible over dark imagery —
// a wider halo underneath, the ink on top.
//
// Every node becomes a path: this markup is assembled by hand rather than by lucide-react, and a
// circle or rect node keeps its geometry in cx/r or width/height, so copying the tag through
// would emit a shape with nothing to draw.
function nodeToSvg([tag, attrs]: IconNode[number], isHalo: boolean): string {
  const isFilled = !!attrs.fill && attrs.fill !== 'none'
  const color = isHalo ? HALO : INK
  const width = DEFAULT_GLYPH_STROKE_WIDTH + (isHalo ? HALO_EXTRA : 0)
  const fillRule = attrs.fillRule ? ` fill-rule="${attrs.fillRule}"` : ''
  const paint =
    isFilled && !isHalo
      ? `fill="${color}" stroke="none"`
      : `fill="${
          isFilled ? color : 'none'
        }" stroke="${color}" stroke-width="${width}" ` +
        'stroke-linecap="round" stroke-linejoin="round"'

  return `<path d="${iconNodeToPathData(tag, attrs)}"${fillRule} ${paint}/>`
}

function toDataUri(nodes: IconNode): string {
  const halo = nodes.map(n => nodeToSvg(n, true)).join('')
  const ink = nodes.map(n => nodeToSvg(n, false)).join('')
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="${CURSOR_PX}" ` +
    `height="${CURSOR_PX}" viewBox="0 0 ${GRID} ${GRID}">${halo}${ink}</svg>`

  return `data:image/svg+xml,${encodeURIComponent(svg)}`
}

// A library sign is its own full-colour tile, already legible over any imagery, so it is drawn
// as-is: no halo, no ink.
function pictogramToDataUri(nodes: IconNode, viewBox: string): string {
  const paths = nodes
    .map(([tag, attrs]) => {
      const fillRule = attrs.fillRule ? ` fill-rule="${attrs.fillRule}"` : ''
      return `<path d="${iconNodeToPathData(tag, attrs)}"${fillRule} fill="${attrs.fill}"/>`
    })
    .join('')
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="${CURSOR_PX}" ` +
    `height="${CURSOR_PX}" viewBox="${viewBox}">${paths}</svg>`

  return `data:image/svg+xml,${encodeURIComponent(svg)}`
}

const cursorFrom = (dataUri: string): string =>
  `url("${dataUri}") ${HOTSPOT} ${HOTSPOT}, ${FALLBACK_CURSOR}`

const cursorCache = new Map<string, string>()

// The cursor for whichever placement tool is armed: the exact glyph the tool's own pallet button
// carries, from the tables the map markers and tags read. iconName is the more specific of the two
// — a VerticalObject is a lift, stairs or a shaft, and a sticker has no location type at all — so
// it wins, matching how the pallet resolves its button. Null when nothing is armed, leaving the
// caller's own cursor chain alone.
export function getToolCursor(
  locationType?: LocationType,
  iconName?: LocationIconName,
): string | null {
  if (!locationType && !iconName) {
    return null
  }
  const key = `${locationType ?? ''}|${iconName ?? ''}`
  const cached = cursorCache.get(key)
  if (cached) {
    return cached
  }

  const pictogram = iconName ? getPictogramGlyph(iconName) : undefined
  const glyph =
    (iconName ? getGlyph(iconName) : undefined) ??
    ICON_DATA[LOCATION_ICON_BY_TYPE[locationType]]
  const cursor = pictogram
    ? cursorFrom(pictogramToDataUri(pictogram.nodes, pictogram.viewBox))
    : glyph
      ? cursorFrom(toDataUri(glyph.nodes))
      : FALLBACK_CURSOR
  // Art still loading is drawn as its stand-in and not kept, so the art replaces it.
  if (!iconName || isIconArtLoaded(iconName)) {
    cursorCache.set(key, cursor)
  }
  return cursor
}

// The two drag affordances. A selected object is something you move, not a link you follow, so
// these replace the hover pointer over it rather than competing with it — see the cursor chain
// in MapBoxViewer.
export const GRAB_CURSOR = 'grab'
export const GRABBING_CURSOR = 'grabbing'

export function isDragAffordanceCursor(cursor: string | null): boolean {
  return cursor === GRAB_CURSOR || cursor === GRABBING_CURSOR
}
